/**
 * useCommandPalette — lógica da command palette (TASK-22, ST-02).
 *
 * Responsabilidades:
 * - Debounce de 200ms na query de busca (Req 7.1).
 * - Filtragem das ações canônicas por papel via `canSeeRoute`/`canDo` (Req 7.4, RNF 9.3).
 * - Busca de contas e oportunidades reais quando query ≥ 2 caracteres (Req 7.1).
 * - Agrupamento e estado de loading.
 * - Navegação por índice (setas ↑↓, Enter executa, Esc fecha).
 *
 * Separação de responsabilidades: este hook não importa React Router nem
 * dispara efeitos DOM. O componente `CommandPalette` consome e direciona.
 *
 * Referências: Req 7, Req 7.1..7.4, RNF 9.3 (a11y teclado), TASK-22.
 */

import { useState, useEffect, useRef } from "react";
import {
  canSeeRoute,
  canDo,
  PALETTE_ACTIONS,
  type RbacContext,
  type PaletteGroup,
} from "../../shared/rbac/index.js";
import { listAccounts } from "../../data/repos/accountsRepo.js";
import { listOpportunities } from "../../data/repos/opportunitiesRepo.js";

// ---------------------------------------------------------------------------
// Tipos públicos do hook
// ---------------------------------------------------------------------------

/** Item normalizado exibido na palette (navegação ou ação rápida). */
export interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly path: string;
  readonly group: PaletteGroup | "Contas" | "Oportunidades";
}

/** Grupo de itens agrupados por categoria. */
export interface PaletteGroup_ {
  readonly label: string;
  readonly items: readonly PaletteItem[];
}

/** Estado e controles expostos pelo hook. */
export interface UseCommandPaletteReturn {
  /** Query atual (sincronizada com input). */
  query: string;
  setQuery: (q: string) => void;
  /** Se há busca de dados em andamento. */
  isLoading: boolean;
  /** Grupos filtrados e agrupados a exibir. */
  groups: readonly PaletteGroup_[];
  /** Lista plana de todos os itens (para navegação por índice). */
  allItems: readonly PaletteItem[];
  /** Índice do item com foco (−1 = foco no input). */
  focusedIndex: number;
  setFocusedIndex: (i: number) => void;
  /** Move o foco para o próximo item. */
  focusNext: () => void;
  /** Move o foco para o item anterior. */
  focusPrev: () => void;
  /** Reseta estado (chamado ao abrir). */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Helpers de filtragem RBAC (Req 7.4)
// ---------------------------------------------------------------------------

/**
 * Filtra as ações canônicas pelo contexto RBAC do usuário.
 * - Ações do tipo "navigate" com `requiredRoute` passam por `canSeeRoute`.
 * - Ações do tipo "action" com `requiredAction` passam por `canDo`.
 * - Ações sem requisito são visíveis para qualquer autenticado.
 */
function filterByRbac(ctx: RbacContext): readonly PaletteItem[] {
  return PALETTE_ACTIONS.filter((action) => {
    if (action.type === "navigate" && action.requiredRoute) {
      return canSeeRoute(action.requiredRoute, ctx);
    }
    if (action.type === "action" && action.requiredAction) {
      return canDo(action.requiredAction, ctx);
    }
    return true;
  }).map((action) => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    path: action.path,
    group: action.group,
  }));
}

/** Agrupa lista plana de itens em grupos ordenados. */
function groupItems(items: readonly PaletteItem[]): readonly PaletteGroup_[] {
  const ORDER: readonly string[] = [
    "Ações",
    "Navegação",
    "Administração",
    "Plataforma",
    "Contas",
    "Oportunidades",
  ];
  const map = new Map<string, PaletteItem[]>();
  for (const item of items) {
    const key = item.group as string;
    if (!map.has(key)) map.set(key, []);
    map.get(key)?.push(item);
  }
  return ORDER.filter((g) => map.has(g)).map((g) => ({
    label: g,
    items: map.get(g) ?? [],
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook principal da command palette.
 *
 * @param rbacCtx - Contexto RBAC derivado do usuário autenticado.
 * @param open    - Se a palette está aberta (reseta estado ao abrir).
 */
export function useCommandPalette(
  rbacCtx: RbacContext,
  open: boolean,
): UseCommandPaletteReturn {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [accountItems, setAccountItems] = useState<PaletteItem[]>([]);
  const [opportunityItems, setOpportunityItems] = useState<PaletteItem[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // --- Reset ao abrir -------------------------------------------------------
  const reset = () => {
    setQuery("");
    setDebouncedQuery("");
    setAccountItems([]);
    setOpportunityItems([]);
    setFocusedIndex(-1);
    setIsLoading(false);
  };

  useEffect(() => {
    if (open) reset();
     
  }, [open]);

  // --- Debounce 200ms (Req 7.1) ---------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  // --- Reset foco ao mudar query --------------------------------------------
  useEffect(() => {
    setFocusedIndex(-1);
  }, [query]);

  // --- Busca real de contas e oportunidades (Req 7.1) -----------------------
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setAccountItems([]);
      setOpportunityItems([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const q = debouncedQuery.toLowerCase();

    Promise.all([
      listAccounts({ search: debouncedQuery, pageSize: 10 }).catch(() => ({
        items: [] as Array<{ id: string; name: string }>,
        totalCount: 0,
      })),
      listOpportunities({ page: 1, pageSize: 50 }).catch(() => ({
        items: [] as Array<{
          id: string;
          title: string;
          opportunityNumber: string;
        }>,
        totalCount: 0,
        page: 1,
        pageSize: 50,
      })),
    ]).then(([accountPage, oppPage]) => {
      if (cancelled) return;

      const accounts = accountPage.items
        .filter((a) => a.name.toLowerCase().includes(q))
        .slice(0, 5)
        .map((a): PaletteItem => ({
          id: `account-${a.id}`,
          label: a.name,
          icon: "accounts",
          path: `/account/${a.id}`,
          group: "Contas",
        }));

      const opportunities = oppPage.items
        .filter(
          (o) =>
            o.title.toLowerCase().includes(q) ||
            o.opportunityNumber.toLowerCase().includes(q),
        )
        .slice(0, 5)
        .map((o): PaletteItem => ({
          id: `opp-${o.id}`,
          label: `${o.opportunityNumber} · ${o.title}`,
          icon: "kanban",
          path: `/opportunity/${o.id}`,
          group: "Oportunidades",
        }));

      setAccountItems(accounts);
      setOpportunityItems(opportunities);
      setIsLoading(false);
    }).catch(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // --- Computação dos grupos filtrados por RBAC (Req 7.4) ------------------
  const rbacCtxRef = useRef(rbacCtx);
  rbacCtxRef.current = rbacCtx;

  const staticFiltered = filterByRbac(rbacCtx);

  // Filtra itens estáticos pela query
  const matchedStatic = query
    ? staticFiltered.filter((i) =>
        i.label.toLowerCase().includes(query.toLowerCase()),
      )
    : staticFiltered;

  // Monta lista plana e grupos
  const hasDataSearch = debouncedQuery.length >= 2;
  const allItemsRaw: PaletteItem[] = [
    ...matchedStatic,
    ...(hasDataSearch ? accountItems : []),
    ...(hasDataSearch ? opportunityItems : []),
  ];

  const groups = groupItems(allItemsRaw);
  const allItems = groups.flatMap((g) => g.items);

  // --- Navegação por índice (RNF 9.3) ---------------------------------------
  const focusNext = () => {
    setFocusedIndex((prev) => {
      if (allItems.length === 0) return -1;
      return prev < allItems.length - 1 ? prev + 1 : 0;
    });
  };

  const focusPrev = () => {
    setFocusedIndex((prev) => {
      if (allItems.length === 0) return -1;
      return prev > 0 ? prev - 1 : allItems.length - 1;
    });
  };

  return {
    query,
    setQuery,
    isLoading,
    groups,
    allItems,
    focusedIndex,
    setFocusedIndex,
    focusNext,
    focusPrev,
    reset,
  };
}
